import React, { FC, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'components/button/Button'
import { numberFormatter } from 'helpers'
import {
    buildRegisteredPackageRows,
    sumRegisteredPackageListTotals,
    sumRegisteredPackageNets
} from 'helpers/buildRegisteredPackageRows'
import { ProductWithAddOns, SelectedPacket } from './enrollFormTypes'
import { Order } from 'storeTypes'
import './registeredPackagesSummary.scss'

type RegisteredPackagesSummaryProps = {
    selectedPackets: SelectedPacket[]
    fullGoalItems?: Order[]
    productListWithAddOns?: ProductWithAddOns[]
    onContinue: () => void
    continueLabel?: string
    discountDataUnavailable?: boolean
    continueDisabled?: boolean
}

export const RegisteredPackagesSummary: FC<RegisteredPackagesSummaryProps> = ({
    selectedPackets,
    fullGoalItems = [],
    productListWithAddOns = [],
    onContinue,
    continueLabel,
    discountDataUnavailable = false,
    continueDisabled = false
}) => {
    const { t } = useTranslation()

    const rows = useMemo(
        () =>
            buildRegisteredPackageRows(
                selectedPackets,
                fullGoalItems,
                productListWithAddOns
            ),
        [selectedPackets, fullGoalItems, productListWithAddOns]
    )

    const totalNet = useMemo(() => sumRegisteredPackageNets(rows), [rows])
    const hideTotal = useMemo(() => rows.some((r) => r.hidePrice), [rows])
    const anyRowHasDiscounts = useMemo(() => rows.some((r) => r.hasDiscounts), [rows])
    const showDiscountTooltip = discountDataUnavailable && anyRowHasDiscounts
    const [tooltipVisible, setTooltipVisible] = useState(false)
    const tooltipRef = useRef<HTMLButtonElement>(null)
    const listTotal = useMemo(() => sumRegisteredPackageListTotals(rows), [rows])
    const displayTotal = discountDataUnavailable ? listTotal : totalNet

    return (
        <div
            className="registered-packages-summary"
            data-testid="registered-packages-summary"
        >
            <h2 className="registered-packages-summary__title">
                {t('enrollment.registeredPackages')}
            </h2>

            <div className="registered-packages-summary__card">
                <ul className="registered-packages-summary__list">
                    {rows.map((row) => (
                        <li
                            key={row.package_id}
                            className="registered-packages-summary__row"
                            data-testid={`registered-package-row-${row.package_id}`}
                        >
                            <div className="registered-packages-summary__row-main">
                                <div className="registered-packages-summary__row-start">
                                    <div className="registered-packages-summary__thumb">
                                        {row.image_url ? (
                                            <img
                                                src={row.image_url}
                                                alt=""
                                                className="registered-packages-summary__thumb-image"
                                            />
                                        ) : (
                                            <span className="registered-packages-summary__thumb-fallback">
                                                {(row.name || '?').charAt(0)}
                                            </span>
                                        )}
                                    </div>
                                    <div className="registered-packages-summary__name-group">
                                        <span className="registered-packages-summary__name">
                                            {row.name}
                                        </span>
                                        {row.size_display && (
                                            <span className="registered-packages-summary__size">
                                                {row.size_display}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <span
                                    className="registered-packages-summary__qty"
                                    data-testid={`registered-package-qty-${row.package_id}`}
                                >
                                    x{row.quantity}
                                </span>
                                <span
                                    className="registered-packages-summary__list-price"
                                    data-testid={`registered-package-list-price-${row.package_id}`}
                                >
                                    {numberFormatter(
                                        row.listTotal,
                                        'CFA',
                                        row.hidePrice
                                    )}
                                </span>
                            </div>
                            {row.discount > 0 && !row.hidePrice && !discountDataUnavailable && (
                                <div className="registered-packages-summary__discount-wrap">
                                    <div
                                        className="registered-packages-summary__discount-chip"
                                        data-testid={`registered-package-discount-${row.package_id}`}
                                    >
                                        -{numberFormatter(row.discount, 'CFA')}
                                    </div>
                                </div>
                            )}
                        </li>
                    ))}
                </ul>

                <div
                    className="registered-packages-summary__total"
                    data-testid="registered-packages-total"
                >
                    <span className="registered-packages-summary__total-label">
                        {t('enrollment.total')}
                    </span>
                    <span className="registered-packages-summary__total-end">
                        <span className="registered-packages-summary__total-amount">
                            {numberFormatter(displayTotal, 'CFA', hideTotal)}
                        </span>
                        {showDiscountTooltip && (
                            <button
                                ref={tooltipRef}
                                className="registered-packages-summary__discount-unavailable"
                                aria-expanded={tooltipVisible}
                                aria-label={t(
                                    'enrollment.discountUnavailableInfo'
                                )}
                                data-testid="discount-unavailable-info"
                                type="button"
                                onClick={() => setTooltipVisible((v) => !v)}
                                onBlur={() => setTooltipVisible(false)}
                            >
                                {'?'}
                                {tooltipVisible && (
                                    <span
                                        className="registered-packages-summary__discount-tooltip"
                                        role="tooltip"
                                    >
                                        {t(
                                            'enrollment.discountUnavailableInfo'
                                        )}
                                    </span>
                                )}
                            </button>
                        )}
                    </span>
                </div>
            </div>

            <div className="registered-packages-summary__footer">
                <Button
                    style="primary"
                    className="registered-packages-summary__continue"
                    size="tall"
                    onClick={onContinue}
                    disable={continueDisabled}
                    data-testid="registered-packages-continue"
                >
                    {continueLabel || t('continue')}
                </Button>
            </div>
        </div>
    )
}
